import { request } from '../api/client';
import { MONTHS } from '../ui/shortDate';

/**
 * Выгрузки на площадки.
 *
 * <p>Их бывает несколько на одну площадку, и различаются они отбором товара:
 * у живого клиента пять прайсов на Дром по ценовым диапазонам, у каждого свой
 * прайс-лист в кабинете площадки и своя цена размещения.
 *
 * <p><b>Пустое поле отбора — «без ограничения», а не «ничего».</b> Выгрузка,
 * у которой стёрли цену, обязана вернуться к отдаче всего склада: пустой прайс
 * площадка примет молча, и объявления пропадут вместе с накопленными
 * просмотрами, за которые и платят.
 */

/**
 * Состояние детали в отборе выгрузки. Коды те же, что в карточке, а слова
 * во множественном числе — так подписан отбор у ориентира: «Новые · Б/У ·
 * Контрактные».
 *
 * <p>Полноту списка стережёт `WordingConsistencyTest`: состояние, которого
 * здесь нет, владелец не смог бы отобрать в прайс-лист вовсе — а узнал бы
 * об этом по тому, что товар уехал не в тот прайс (задача 0039).
 */
export const CONDITIONS = [
  { code: 'NEW', name: 'новые' },
  { code: 'USED', name: 'б/у' },
  { code: 'REFURBISHED', name: 'восстановленные' },
  { code: 'CONTRACT', name: 'контрактные' },
] as const;

/**
 * Настройки сборки прайса — не отбора.
 *
 * <p>Отбор говорит, какой товар уедет в этот прайс-лист; настройки — каким
 * он уедет. Площадка берёт комиссию, и продавцы закладывают её в цену
 * объявления: у живого клиента на прайсе Авито стоит −20 %. Цена на складе,
 * на витрине и у продавца при этом не меняется — иначе комиссия площадки
 * поднимет цену тому же товару в зале и по телефону.
 *
 * <p><b>Следующая настройка добавляется сюда полем</b> и полем на экране;
 * сервер разбирает и пишет их по составу этой же записи, и путь до сборки
 * прайса уже проложен.
 *
 * <p>Числами, а не строками: на сервере это `numeric`, и Jackson отдаёт его
 * числом JSON. Тип, объявленный строкой, компилятор не поправит — он верит
 * объявлению, — а первый же `.trim()` упадёт уже в браузере. Ровно так
 * молча ломались кнопки отбора.
 */
export interface FeedSettings {
  /** Плюс — наценка, минус — скидка; пусто или ноль — цена уезжает как есть. */
  pricePercent: number | null;
  /** Шаг округления результата; пусто или ноль — не округлять. */
  priceRounding: number | null;
  /**
   * Сколько снимков уходит в объявление.
   *
   * <p>`null` — не задано, и уезжают прежние десять: настройка появилась
   * позже прайсов, и молча изменить их она не должна. Ноль — без
   * ограничения, уедут все снимки позиции.
   */
  photoLimit: number | null;
  /**
   * Дописывать ли к описанию объявления стоимость установки детали.
   *
   * <p>`null` и `false` — не дописывать. Услуга у клиента заведена и стоит
   * денег, а покупатель о ней не знал вовсе: «Цена установки» есть
   * в карточке и в отборе выгрузки, а до объявления не доезжала ни одной
   * строкой.
   */
  installationNote: boolean | null;
  /** Текст приписки; `{цена}` в нём заменяется стоимостью установки. */
  installationTemplate: string | null;
  /**
   * Выгружать ли товар, по которому ожидается поступление: он привязан
   * к поставке в пути и на складе его ещё нет.
   *
   * <p>`null` и `false` — не выгружать; новая выгрузка на Дром заводится
   * с `true`, как у Bazon (задача 0339). Меняет не только вид объявления,
   * но и состав прайса, — поэтому её состояние уходит и в счётчик позиций.
   */
  expectedGoods: boolean | null;
  /**
   * Приставка, которая встаёт **в начало** описания такого объявления.
   * Пустая — товар уходит без неё, как у Bazon с пустым полем (задача 0339).
   */
  expectedGoodsNote: string | null;
  /**
   * «Выгружать товары в резерве»: уходит ли позиция, целиком отложенная
   * под клиента. `null` и `false` — нет, как у Bazon по умолчанию (задача
   * 0339). Состав прайса меняет — уходит и в счётчик.
   */
  reservedGoods?: boolean | null;
  /** Приставка резерва — первой строкой описания; пустая — без неё. */
  reservedGoodsNote?: string | null;
  /**
   * Наименования, которым в объявление дописываются снимки машины-донора.
   *
   * <p>Пусто — никому, как было до появления настройки. Выборочность и есть
   * смысл этой настройки: у двигателя и коробки состояние машины — половина
   * объявления, а у фары те же фотографии лишние.
   */
  donorPhotoKinds: number[] | null;
}

/**
 * Куда в тексте приписки встаёт цена.
 *
 * <p>Повторяет серверную константу `FeedSettings.PRICE_PLACEHOLDER`: подстановку
 * набирает владелец руками, и разойдись эти два написания — приписка молча
 * уехала бы к покупателю без суммы.
 */
export const PRICE_PLACEHOLDER = '{цена}';

/**
 * Текст приписки, пока владелец не написал свой.
 *
 * <p>Поле открывается заполненным: включённая приписка с пустым текстом
 * была бы включённой возможностью, которая ничего не делает. Формулировка
 * та же, что у системы, с которой переходят клиенты.
 */
export const DEFAULT_INSTALLATION_TEMPLATE =
  `Стоимость установки на нашем автосервисе: ${PRICE_PLACEHOLDER} р.`;

/** «Округлять до» — те же шаги, что у системы, с которой переходят клиенты. */
export const ROUNDING_STEPS = ['0.01', '0.1', '10', '100'] as const;

export interface Feed {
  id: number;
  marketplace: string;
  title: string;
  status: string;
  hasCredentials: boolean;
  plaintextSecret: boolean;
  hasFeed: boolean;
  /**
   * Читаемое имя файла в конце ссылки — «drom-parts.xml».
   *
   * <p>`null` — имени не задавали, и ссылка кончается токеном. Это рабочее
   * состояние, а не незаполненное поле: имя нужно тому, кто переносит адрес
   * в кабинет площадки руками.
   */
  feedFileName: string | null;
  /** Что уезжает: «PART» — запчасти, «WHEEL» — шины и диски. */
  productLine: 'PART' | 'WHEEL';
  /** Как собирается файл: наценка на прайс-лист и округление. */
  settings: FeedSettings;
  /**
   * Номер прайс-листа в кабинете площадки; `null` — не задан, и дельты
   * для выгрузки не уходят. В отличие от ключа это не секрет: его владелец
   * читает в адресе прайс-листа и должен видеть, что записано.
   */
  packetId: string | null;
  lastError: string | null;
  /**
   * Итог последней отправки дельт на Дром (задача 0268). `null` — про выгрузку
   * отправка не говорится: выключена или не Дром. Слова — `deliveryLine`;
   * сырой ответ площадки остаётся в `lastError`.
   */
  deliveryStatus: DeliveryStatus | null;
  /**
   * Когда площадка последний раз забрала прайс по постоянной ссылке.
   * `null` — не забирала ни разу.
   *
   * <p>Это не то же самое, что отправленная дельта: «мы отправили»
   * и «у нас забрали» — разные события, и на вопрос «прайс вообще уехал?»
   * отвечает только второе.
   */
  lastDownloadAt: string | null;
  /**
   * Когда выгрузку удалили. `null` — живая.
   *
   * <p>Удалённая не приходит в обычном списке вовсе: её нет ни в перечне,
   * ни в счётчиках. Приходит она только по отдельному запросу — ради ответа
   * на вопрос «эта выгрузка вообще работала и когда её последний раз
   * забирали»: строка остаётся именно поэтому.
   */
  deletedAt: string | null;
  /**
   * Цена приходит числом, а не строкой: на сервере это {@code numeric},
   * и Jackson отдаёт его числом JSON. Тип, объявленный строкой, компилятор
   * не поправит — он верит объявлению, — а первый же {@code .trim()} упадёт
   * уже в браузере. Так и вышло: кнопки отбора молча отвечали «не удалось»,
   * потому что падали до запроса.
   */
  priceFrom: number | null;
  priceTo: number | null;
  conditions: string[];
  warehouseIds: number[];
  kindIds: number[];
  kindsExcluded: boolean;
  brandIds: number[];
  brandsExcluded: boolean;
  /**
   * Свои условия владельца: «колонка → значение», точное равенство.
   *
   * <p>Шесть зашитых условий (цена, состояние, склады, наименования, марки)
   * покрывают не всё, а каждое седьмое означало бы релиз. Витрина склада
   * к этому времени отбирает по двадцати девяти колонкам, и выгрузка берёт
   * тот же механизм: список колонок закрыт сервером, неизвестное имя
   * отвергается.
   */
  filterColumns: Record<string, string>;
  /** То же вхождением: «Nok» находит Nokian. */
  filterWords: Record<string, string>;
}

/** Отправляем строками: сервер разберёт их в numeric сам. */
export interface FeedFilter {
  priceFrom: string | null;
  priceTo: string | null;
  conditions: string[];
  warehouseIds: number[];
  kindIds: number[];
  kindsExcluded: boolean;
  brandIds: number[];
  brandsExcluded: boolean;
  columns: Record<string, string>;
  words: Record<string, string>;
}

/**
 * Сколько позиций попадёт в выгрузку с таким отбором.
 *
 * <p>Ради этого числа запрос и существует. Список по видам деталей
 * на неразобранном справочнике даёт пустой прайс: у только что переехавшего
 * клиента вид не заполнен ни у одной позиции, пока наименования
 * не сопоставлены. Площадка пустой прайс примет молча, и объявления пропадут
 * вместе с просмотрами — узнают об этом через сутки.
 */
export function countMatching(
  filter: FeedFilter,
  productLine: 'PART' | 'WHEEL',
  expectedGoods: boolean,
  reservedGoods: boolean,
): Promise<Counted> {
  // Линия обязательна: без неё счётчик считал запчасти всегда и у выгрузки
  // колёс обещал 35 835 позиций там, где уезжало 60.
  //
  // Переключатель ожидаемого товара едет рядом с отбором, хотя живёт
  // в настройках сборки: состав прайса он меняет, и счётчик, о нём
  // не знающий, обещал бы меньше, чем уедет площадке. Как и весь остальной
  // отбор, он берётся из формы — до сохранения.
  //
  // Резерв — по той же причине (задача 0339): отложенное целиком уходит
  // в файл только при включённом переключателе.
  return request<Counted>('/api/marketplace-accounts/filter/count', {
    method: 'POST',
    body: { ...filter, productLine, expectedGoods, reservedGoods },
  });
}

/**
 * Ответ счётчика.
 *
 * <p>`zeroPrice` — сколько из попавших в файл стоят 0: они идут на площадку,
 * как у Bazon (задача 0339), и экран называет их красной строкой рядом
 * со счётчиком. Сервер прежней версии поля не несёт — тогда строки нет.
 */
export interface Counted {
  parts: number;
  zeroPrice?: number;
}

/**
 * По каким колонкам сервер умеет отбирать — списком с самого сервера.
 *
 * <p>Повторённый на клиенте, список разошёлся бы с ним на первой же новой
 * колонке, и экран предлагал бы условие, которого нет: сервер отвечает
 * «по этой колонке отбор не делается», а владелец видит прежний прайс
 * и решает, что отбор сломан. Ровно этим болело меню колонки на витрине.
 *
 * <p>Страницей в одну строку, а не своим эндпоинтом: список едет вместе
 * с выдачей склада, и второй источник того же знания — это то, что здесь
 * и избегается.
 */
export function filterableColumns(line: 'PART' | 'WHEEL'): Promise<string[]> {
  const path = line === 'WHEEL'
    ? '/api/wheels?page=0&size=1'
    : '/api/parts/catalog?page=0&size=1';
  // Ответ без ожидаемого ключа — это чужой ответ, а не пустой список:
  // разбирать его дальше значит уронить экран целиком на `undefined.length`.
  return request<{ filterable?: string[] }>(path).then((page) => page.filterable ?? []);
}

export function listFeeds(): Promise<Feed[]> {
  return request<Feed[]>('/api/marketplace-accounts');
}

/**
 * Удалённые выгрузки — отдельным запросом, а не признаком в общем списке.
 *
 * <p>Удаление помечает строку, а не убирает её: удалить запись значило бы
 * унести историю отправок, а спрашивают именно её — «эта выгрузка вообще
 * работала и когда её последний раз забирали». Но в списке и в счётчиках
 * удалённой быть не должно, иначе «удалена» значит «помечена», и владелец
 * пересчитывает свои прайс-листы глазами.
 */
export function listDeletedFeeds(): Promise<Feed[]> {
  return request<Feed[]>('/api/marketplace-accounts?deleted=true');
}

/**
 * Переименовывает выгрузку.
 *
 * <p>Ссылку это не трогает: её прописывает в кабинете площадки техспециалист
 * руками, и связать её с названием значило бы превратить исправление опечатки
 * в остановку выгрузки на несколько дней.
 */
export function renameFeed(id: number, title: string): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/title`, {
    method: 'PUT',
    body: { title },
  });
}

/**
 * Включает выгрузку или выключает её.
 *
 * <p>Выключенная не отдаёт прайс вовсе — площадка получает «нет такой»
 * и попробует позже. Не пустой прайс: пустой файл она читает буквально,
 * «этих товаров больше нет», и снимает объявления вместе с накопленными
 * просмотрами.
 */
export function setFeedStatus(id: number, status: 'ACTIVE' | 'PAUSED'): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/status`, {
    method: 'PUT',
    body: { status },
  });
}

/**
 * Удаляет выгрузку.
 *
 * <p>На сервере это пометка: ссылка перестаёт работать, из списка выгрузка
 * пропадает, название и имя файла освобождаются, а отметки о заборе прайса
 * и об отправках остаются — их и спрашивают, когда объявления пропали.
 */
export function deleteFeed(id: number): Promise<void> {
  return request<void>(`/api/marketplace-accounts/${id}`, { method: 'DELETE' });
}

/**
 * Состояние выгрузки словами.
 *
 * <p>«PAUSED» на экране не значит ничего, а разница видна площадке: у
 * выключенной прайс не забирают вовсе. Ошибку показываем отдельной строкой,
 * она про последнюю отправку, а не про состояние.
 */
export function statusLabel(feed: Feed): string {
  if (feed.status === 'PAUSED') {
    return 'выключена';
  }
  // ERROR пишет не человек, а неудачная отправка. Показать его «работает»
  // значило бы соврать про состояние, а «выключена» — соврать про причину.
  return feed.status === 'ERROR' ? 'ошибка' : 'работает';
}

/** Когда выгрузку удалили — тем же коротким видом, что и отметка о заборе. */
export function deletedMark(feed: Feed): string {
  return feed.deletedAt === null ? '' : `Удалена ${moment(feed.deletedAt)}`;
}

/**
 * Заводит кабинет площадки.
 *
 * <p>До этого экран писал «кабинет площадки заводит владелец» и не давал
 * этого сделать: эндпоинт был, звать его было некому, и новый клиент
 * оставался без прайса вовсе — то есть без того, ради чего переезжал.
 *
 * @param packetId номер прайс-листа из кабинета площадки; нужен дельтам
 *                 по API, постоянная ссылка работает и без него
 */
export function createFeed(
  title: string,
  packetId: string,
  productLine: 'PART' | 'WHEEL',
): Promise<Feed> {
  return request<Feed>('/api/marketplace-accounts', {
    method: 'POST',
    body: {
      marketplace: 'DROM',
      title,
      // Тем же правилом, что и правка в карточке: адрес прайс-листа даёт
      // номер, пусто — номера нет. Нераспознанное уходит как набрано —
      // сервер отвергнет его словами, а не запишет.
      settings: packetId.trim() === ''
        ? null
        : JSON.stringify({ packetId: normalizePacketId(packetId) ?? packetId }),
      productLine,
    },
  });
}

/**
 * Сохраняет настройки сборки прайса.
 *
 * <p>Отдельным запросом от отбора, потому что это разные решения: отбор
 * меняет состав прайса, наценка — цену в нём. Сервер кладёт настройки
 * слиянием, поэтому номер прайс-листа в кабинете площадки остаётся на месте.
 *
 * <p>Пустое поле уезжает как `null` — «не задано». Ноль тут значил бы то же
 * самое, но отличить «стёрли» от «поставили ноль» на сервере было бы нечем.
 */
export function setSettings(id: number, settings: FeedSettingsInput): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/settings`, {
    method: 'PUT',
    body: settings,
  });
}

/**
 * То же, но как его набирают: строками.
 *
 * <p>Отправляем строками, как и границы цены в отборе: сервер разберёт их
 * в `numeric` сам, а превращать «-20» в число на клиенте значит завести
 * второе место, где решается, что считать пустым.
 */
export interface FeedSettingsInput {
  pricePercent: string | null;
  priceRounding: string | null;
  photoLimit: string | null;
  /** Переключатель уезжает как есть: у «да/нет» пустого состояния нет. */
  installationNote: boolean;
  /** Пусто — «текст не задавали», и сервер возьмёт формулировку по умолчанию. */
  installationTemplate: string | null;
  /** Переключатель «выгружать товары, по которым ожидается поступление». */
  expectedGoods: boolean;
  /** Пусто — товар уходит без приставки (задача 0339). */
  expectedGoodsNote: string | null;
  /** Переключатель «Выгружать товары в резерве». */
  reservedGoods: boolean;
  /** Пусто — отложенное уходит без приставки. */
  reservedGoodsNote: string | null;
  /**
   * Наименования со снимками машины-донора.
   *
   * <p>Списком, а не строкой: его собирает тот же выбор наименований, что
   * и отбор выгрузки. Пустой список — «не дописывать никому», и это
   * законное состояние, а не незаполненное поле.
   */
  donorPhotoKinds: number[];
}

/** Запятая в поле — та же десятичная точка: на телефоне её и набирают. */
export function decimalOrNull(typed: string): string | null {
  const clean = typed.trim().replace(',', '.');
  return clean === '' ? null : clean;
}

/**
 * Целое из поля — или «не задано».
 *
 * <p>Отдельно от `decimalOrNull`, потому что запятую тут исправлять нечего:
 * снимков бывает три, а не три с половиной, и «3,5» должно уехать на сервер
 * как есть и получить отказ, а не превратиться в число, которого владелец
 * не набирал.
 */
export function wholeOrNull(typed: string): string | null {
  const clean = typed.trim();
  return clean === '' ? null : clean;
}

export function setFilter(id: number, filter: FeedFilter): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/filter`, {
    method: 'PUT',
    body: filter,
  });
}

/**
 * Ссылка на прайс: путь и полный адрес.
 *
 * <p>Полный отдаёт сервер из `app.public-url` — его и передают
 * техспециалисту площадки. По одному пути тот не сходит никуда,
 * а владелец дописывал домен руками.
 */
export interface FeedLink {
  path: string | null;
  url: string | null;
}

export function feedUrl(id: number): Promise<FeedLink> {
  return request<FeedLink>(`/api/marketplace-accounts/${id}/feed-url`);
}

/**
 * Задаёт имя файла прайса — читаемый хвост ссылки.
 *
 * <p>Адрес прописывает в кабинете площадки её техспециалист руками, и хвост
 * из сорока случайных символов он переносит с ошибками — а ошибку видно
 * только по тому, что объявления не появились. Секрет при этом остаётся
 * на месте: смены ссылки тут не происходит, прежний адрес продолжает
 * работать.
 *
 * <p>Пустое поле снимает имя. Уезжает оно пустой строкой, а сервер пишет
 * `NULL`: две выгрузки без имени не должны сталкиваться в уникальном индексе.
 */
export function setFeedFileName(id: number, fileName: string): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/feed-file`, {
    method: 'PUT',
    body: { fileName },
  });
}

export function rotateFeedUrl(id: number): Promise<FeedLink> {
  return request<FeedLink>(`/api/marketplace-accounts/${id}/feed-url`, {
    method: 'POST',
  });
}

/**
 * Записывает ключ синхронизации кабинета площадки.
 *
 * <p>Только запись: прочитать ключ нельзя ни одним эндпоинтом, и это часть
 * защиты — иначе право смотреть настройки превращается в доступ к кабинету
 * клиента. Хранится он зашифрованным (AES-GCM), ключ шифрования живёт вне
 * базы.
 *
 * <p>Без него дельты по API не уходят вовсе: цена, остаток и снятие
 * с продажи доезжают до площадки только с полным прайсом, а его забирают
 * раз в трое суток.
 */
export function setCredentials(id: number, secret: string): Promise<void> {
  return request<void>(`/api/marketplace-accounts/${id}/credentials`, {
    method: 'PUT',
    body: { secret },
  });
}

/** Откуда брать номер прайс-листа: одна строка и под полем, и в форме заведения. */
export const PACKET_ID_HINT =
  'Номер виден в адресе прайс-листа в кабинете Дрома: …/packet/номер/recurrent-update. '
  + 'Он появляется после того, как специалист Дрома разместит прайс по нашей ссылке.';

/**
 * Номер из того, что набрали: пусто — снять; адрес прайс-листа целиком даёт
 * номер из него. `undefined` — не распознано (пробел внутри, длиннее 32 знаков).
 *
 * <p>Те же правила, что у сервера: сервер всё равно проверяет, а экран
 * гасит кнопку заранее и называет причину.
 */
export function normalizePacketId(typed: string): string | undefined {
  let value = typed.trim();
  if (value === '') return '';
  const fromUrl = /\/packet\/([^/?#\s]+)/.exec(value);
  if (fromUrl?.[1]) value = fromUrl[1];
  return /^[A-Za-z0-9_-]{1,32}$/.test(value) ? value : undefined;
}

/**
 * Почему кнопка «Сохранить номер» погашена; `null` — можно нажимать.
 * Одно выражение и на кнопку, и на подпись под ней.
 */
export function packetIdObstacle(typed: string, current: string | null): string | null {
  const next = normalizePacketId(typed);
  if (next === undefined) {
    return 'Номер не распознан: впишите номер из адреса прайс-листа или вставьте адрес целиком '
      + '(не больше 32 знаков, без пробелов)';
  }
  if (next === (current ?? '')) return 'Номер не изменился';
  return null;
}

/**
 * Вписывает, заменяет или снимает номер прайс-листа у заведённой выгрузки.
 *
 * <p>Пустая строка снимает номер, и дельты для этой выгрузки перестают
 * уходить; полный прайс по ссылке работает как раньше. Отдельным запросом
 * от настроек сборки: тело `PUT /settings` остаётся полным и номера не несёт.
 */
export function setPacketId(id: number, packetId: string): Promise<Feed> {
  return request<Feed>(`/api/marketplace-accounts/${id}/packet-id`, {
    method: 'PUT',
    body: { packetId: normalizePacketId(packetId) ?? packetId },
  });
}

/**
 * Отбор словами — то, что видно в списке без раскрытия карточки.
 *
 * <p>Владельцу с пятью выгрузками важно с первого взгляда понять, какая
 * из них какой кусок склада забирает: «до 1 000 ₽» и «от 50 000 ₽» различаются
 * мгновенно, а «есть фильтр» не значит ничего.
 */
export function filterSummary(feed: Feed): string {
  const parts: string[] = [];

  const from = feed.priceFrom === null ? null : Number(feed.priceFrom);
  const to = feed.priceTo === null ? null : Number(feed.priceTo);
  if (from !== null && to !== null) {
    parts.push(`${money(from)}—${money(to)} ₽`);
  } else if (from !== null) {
    parts.push(`от ${money(from)} ₽`);
  } else if (to !== null) {
    parts.push(`до ${money(to)} ₽`);
  }

  if (feed.conditions.length > 0) {
    parts.push(
      feed.conditions
        .map((code) => CONDITIONS.find((c) => c.code === code)?.name ?? code)
        .join(', '),
    );
  }

  if (feed.warehouseIds.length > 0) {
    parts.push(`складов: ${feed.warehouseIds.length}`);
  }

  // Направление списка называется словом: «наименований: 3» не отвечает
  // на вопрос, выгружаются они или наоборот исключены, а решения
  // это противоположные.
  if (feed.kindIds.length > 0) {
    parts.push(`${feed.kindsExcluded ? 'кроме' : 'только'} наименований: ${feed.kindIds.length}`);
  }
  if (feed.brandIds.length > 0) {
    parts.push(`${feed.brandsExcluded ? 'кроме' : 'только'} марок: ${feed.brandIds.length}`);
  }

  // Свои условия названы числом, а не перечислены: колонок бывает
  // несколько, и подпись под заголовком должна оставаться в одну строку.
  const own = Object.keys(feed.filterColumns ?? {}).length
      + Object.keys(feed.filterWords ?? {}).length;
  if (own > 0) {
    parts.push(`своих условий: ${own}`);
  }

  // Не «фильтров нет»: пустой отбор — это осмысленное состояние, весь склад.
  return parts.length === 0 ? 'весь склад' : parts.join(' · ');
}

function money(value: number): string {
  return value.toLocaleString('ru-RU');
}

export type DeliveryState =
  | 'NOT_CONNECTED'
  | 'NO_PACKET'
  | 'NO_KEY'
  | 'WAITING'
  | 'ACCEPTED'
  | 'REFUSED';

export type DeliveryCode =
  | 'PACKET_NOT_FOUND'
  | 'AUTH_FAILED'
  | 'EMPTY_REQUEST'
  | 'UNKNOWN_REASON'
  | 'HTTP_ERROR'
  | 'UNRECOGNIZED'
  | 'TIMEOUT'
  | 'NO_CONNECTION'
  | 'HOST_UNKNOWN'
  | 'CONNECTION_BROKEN'
  | 'TOO_BIG';

/** Вид тела ответа Дрома — как его узнал сервер. */
export type AnswerForm = 'EMPTY' | 'JSON' | 'PAGE' | 'MARKUP' | 'TEXT';

/**
 * `at` — время последней попытки при любом исходе; `code` — только при отказе.
 * `httpStatus`, `answerForm` и `detail` — подробности отказа: статус ответа,
 * вид тела и то, что показать как есть (незнакомый код, начало незнакомого
 * ответа, заголовок страницы, текст сбоя соединения). Ответ прежней версии
 * сервера этих полей не несёт — отсюда необязательность.
 */
export interface DeliveryStatus {
  state: DeliveryState;
  at: string | null;
  code: DeliveryCode | null;
  httpStatus?: number | null;
  answerForm?: AnswerForm | null;
  detail?: string | null;
}

/**
 * Отказы, которые красят строку: у знакомых трёх есть действие владельца,
 * а незнакомый код — это отказ площадки, которого мы не понимаем, и его
 * надо показать нам, а не ждать, что повторится само.
 */
const RED: DeliveryCode[] = ['PACKET_NOT_FOUND', 'AUTH_FAILED', 'EMPTY_REQUEST', 'UNKNOWN_REASON'];

/** Как назвать незнакомый ответ: сам текст, страница или пустота. */
function answerShown(status: DeliveryStatus): string {
  const detail = status.detail ?? '';
  if (status.answerForm === 'EMPTY' || detail === '') {
    return 'пустым ответом';
  }
  if (status.answerForm === 'PAGE') {
    return `веб-страницей «${detail}» вместо ответа API`;
  }
  return `«${detail}»`;
}

/** Ответ с HTTP-статусом не 200 и без кода отказа — по классу статуса. */
function httpErrorText(status: DeliveryStatus, when: string): string {
  const code = status.httpStatus ?? 0;
  const body = status.answerForm === 'PAGE' && status.detail
    ? ` — вместо ответа API пришла веб-страница «${status.detail}»`
    : status.detail ? `: «${status.detail}»` : '';
  if (code >= 500) {
    return `Дром ответил ошибкой сервера HTTP ${code}${when}${body}. Это сбой на стороне `
      + 'площадки; отправка повторится сама.';
  }
  if (code >= 400) {
    return `Дром отверг запрос — HTTP ${code}${when}${body}. Отправка повторится; если ответ `
      + 'не меняется — сообщите нам.';
  }
  return `Дром ответил HTTP ${code} вместо принятия${when}${body}. Отправка повторится; `
    + 'если ответ не меняется — сообщите нам.';
}

/**
 * Итог последней отправки на Дром словами — единственное место слов.
 *
 * <p>Знакомый код Дрома (`ERROR_REASON_…`) сюда не попадает: человеку он
 * ничего не говорит, его заменяют слова, а сам он остаётся в раскрывающемся
 * «Ответ Дрома». Незнакомый же показан **как есть** — решение владельца
 * продукта от 12 октября 2026 (задача 0268): «показывать реальную проблему,
 * а не общими словами». Поэтому и «Дром не ответил» распалось на то, что
 * случилось на самом деле: не дождались ответа, не соединились, не нашли
 * адрес, связь оборвалась, ответил ошибкой с таким-то статусом.
 *
 * <p>Слова — предложение разведки 12.10.2026 и исполнителя, а не перенос
 * с Bazon (там слов для этого места не снято). Про «новые объявления» здесь
 * нет ни слова намеренно: что Дром делает с ними по API, живой пробой не снято.
 *
 * @returns `null`, если про выгрузку отправка не говорится
 */
export function deliveryLine(feed: Feed): { text: string; error: boolean } | null {
  const status = feed.deliveryStatus;
  if (!status) {
    return null;
  }
  const at = status.at === null ? '' : moment(status.at);
  switch (status.state) {
    case 'NOT_CONNECTED':
      return {
        text: 'Обновление по API не подключено. Цены и остатки у площадки обновятся '
          + 'только с полным прайсом — он забирается раз в трое суток.',
        error: false,
      };
    case 'NO_PACKET':
      return {
        text: 'Дельты не уходят: не указан номер прайс-листа. Он появится в кабинете '
          + 'Дрома после того, как специалист разместит прайс по нашей ссылке.',
        error: false,
      };
    case 'NO_KEY':
      return { text: 'Дельты не уходят: не задан ключ синхронизации.', error: false };
    case 'WAITING':
      return {
        text: 'Подключено у нас. Дельта уйдёт при первом изменении цены, остатка '
          + 'или продаже позиции этого прайс-листа.',
        error: false,
      };
    case 'ACCEPTED':
      return { text: `Последняя дельта принята Дромом — ${at}.`, error: false };
    case 'REFUSED':
      break;
  }
  // Кода нет только в ответе, который сервер собрал не по договору: сказать
  // правду можно лишь то, что попытка не удалась.
  const code: DeliveryCode = status.code ?? 'UNRECOGNIZED';
  const when = at === '' ? '' : ` (${at})`;
  const detail = status.detail ? `: ${status.detail}` : '';
  const http = status.httpStatus ? `HTTP ${status.httpStatus}, ` : '';
  const text: Record<DeliveryCode, () => string> = {
    PACKET_NOT_FOUND: () =>
      `Дром отказал${when}: прайс-лист не подключён к API. Напишите в поддержку Дрома `
      + `и попросите включить обновление по API для этого прайс-листа `
      + `(номер ${feed.packetId ?? '—'}). Пока не включат, изменения уедут только полным прайсом.`,
    AUTH_FAILED: () =>
      `Дром не принял ключ${when}. Проверьте ключ синхронизации — его выдаёт поддержка `
      + 'Дрома на кабинет, он выглядит как UUID. Заменить можно ниже.',
    EMPTY_REQUEST: () =>
      `Дром получил запрос без данных или без ключа${when}. Задайте ключ заново; `
      + 'если повторяется — сообщите нам.',
    UNKNOWN_REASON: () =>
      `Дром отказал с кодом, которого мы не знаем${when}: ${status.detail ?? '—'}. `
      + 'Что он значит, нам неизвестно — сообщите нам; ответ целиком — в «Ответ Дрома» ниже.',
    HTTP_ERROR: () => httpErrorText(status, when),
    // Без статуса — запись до 12 октября 2026: ответил ли Дром или связь
    // не установилась, по ней не понять, и «Дром ответил» было бы неправдой.
    UNRECOGNIZED: () => (status.httpStatus
      ? `Дром ответил незнакомым образом${when} (${http}мы не знаем, приняты ли изменения): `
        + `${answerShown(status)}. Отправка повторится; если повторяется — сообщите нам.`
      : `Последняя отправка на Дром не прошла${when}: «${status.detail || '—'}». По старой `
        + 'записи не понять, ответил ли Дром или не установилась связь; отправка повторится, '
        + 'и следующая попытка назовёт причину точно.'),
    TIMEOUT: () =>
      `Дром не ответил вовремя${when}: соединение установилось, а ответа не дождались. `
      + 'Отправка повторится сама.',
    NO_CONNECTION: () =>
      `Не удалось соединиться с Дромом${when}${detail}. Сервер площадки не принял `
      + 'соединение; отправка повторится сама.',
    HOST_UNKNOWN: () =>
      `Не удалось найти адрес Дрома${when}: имя площадки не разрешилось. Похоже, у нас `
      + 'нет выхода в интернет или сбой DNS; отправка повторится сама, если повторяется — '
      + 'сообщите нам.',
    CONNECTION_BROKEN: () =>
      `Связь с Дромом оборвалась${when}${detail}. Отправка повторится сама.`,
    TOO_BIG: () =>
      `Изменений накопилось больше, чем принимает Дром за раз${when}. `
      + 'Они уедут полным прайсом.',
  };
  return { text: (text[code] ?? text.UNRECOGNIZED)(), error: RED.includes(code) };
}

/**
 * Когда площадка последний раз забирала прайс.
 *
 * <p>Первый вопрос при подключении клиента и первый, когда объявления
 * пропали: «прайс вообще уехал?». До этой отметки на него отвечал
 * разработчик по логам приложения — то есть клиент ждал человека, чтобы
 * узнать факт, который система знает.
 *
 * <p><b>«Не забирали» — это ответ, а не пустое место.</b> У новой выгрузки
 * так и есть: ссылку только что выдали и техспециалист площадки ещё
 * не прописал её в кабинете. Пустая клетка на этом месте читалась бы как
 * «экран не знает», а «01.01.1970» — как поломка.
 */
export function downloadMark(feed: Feed): string {
  return feed.lastDownloadAt === null
    ? 'Прайс не забирали'
    : `Скачан ${moment(feed.lastDownloadAt)}`;
}

/*
 * Таблица месяцев общая (`ui/shortDate`), а не своя на экран: короткое имя
 * в `ru-RU` зависит от сборки ICU («сент.» против «сен»), поэтому `Intl` тут
 * не годится, — а три копии таблицы уже разъехались падежом. Число месяца
 * не годится вовсе: «04.09» рядом со временем читается как ещё одно время.
 */

/** Время местное — забор идёт ночью, и час здесь смысловой. */
function moment(iso: string): string {
  const at = new Date(iso);
  const day = String(at.getDate()).padStart(2, '0');
  const hours = String(at.getHours()).padStart(2, '0');
  const minutes = String(at.getMinutes()).padStart(2, '0');
  return `${day} ${MONTHS[at.getMonth()]}, ${hours}:${minutes}`;
}
